import React, { useState } from 'react';
import { ShieldCheck, ArrowLeft, Lock, Mail, Eye, EyeOff, LogIn, AlertCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

// Firebase Auth Imports
import { auth } from "../firebase"; 
import { signInWithEmailAndPassword } from "firebase/auth";

const Login = () => {
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false); // Added loading state

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      // Firebase Sign In
      await signInWithEmailAndPassword(auth, email, password);
      
      // Set local flag for route protection (Firebase also manages this automatically)
      localStorage.setItem('isStaffAuthenticated', 'true');
      
      navigate('/dashboard');
    } catch (err) {
      // Handle common Firebase Auth errors
      if (err.code === 'auth/user-not-found' || err.code === 'auth/wrong-password' || err.code === 'auth/invalid-credential') {
        setError('Invalid work email or Password.');
      } else {
        setError('Connection error. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-orange-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md relative z-10">
        <button 
          onClick={() => navigate('/')}
          className="mb-6 flex items-center gap-2 text-orange-900 font-bold hover:text-orange-700 transition-all group"
        >
          <ArrowLeft size={20} className="group-hover:-translate-x-1 transition-transform" />
          Public Website
        </button>

        <div className="bg-white rounded-[2rem] shadow-2xl overflow-hidden border-t-8 border-orange-600">
          <div className="p-8 pb-4 text-center">
            <div className="w-16 h-16 bg-orange-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <ShieldCheck size={32} className={`text-orange-600 ${loading ? 'animate-pulse' : ''}`} />
            </div>
            <h1 className="text-2xl font-serif font-black text-stone-800 uppercase tracking-tight">Staff Portal</h1>
            <p className="text-stone-500 text-sm mt-1">Internal Administration Access</p>
          </div>

          <form onSubmit={handleLogin} className="px-8 py-4 space-y-4">
            {error && (
              <div className="bg-red-50 text-red-600 p-3 rounded-xl text-xs font-bold flex items-center gap-2 border border-red-100">
                <AlertCircle size={16} /> {error}
              </div>
            )}

            <div className="space-y-1">
              <label className="text-xs font-black text-stone-500 uppercase ml-1">Work Email</label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" size={18} />
                <input 
                  type="email" 
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 bg-stone-50 border border-stone-200 rounded-xl focus:ring-2 focus:ring-orange-500 outline-none transition-all"
                  placeholder="name@temple.org"
                  disabled={loading}
                  required
                />
              </div>
            </div>

            <div className="space-y-1">
              <label className="text-xs font-black text-stone-500 uppercase ml-1">Password</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" size={18} />
                <input 
                  type={showPassword ? "text" : "password"} 
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full pl-10 pr-12 py-3 bg-stone-50 border border-stone-200 rounded-xl focus:ring-2 focus:ring-orange-500 outline-none transition-all"
                  placeholder="••••••••"
                  disabled={loading}
                  required
                />
                <button 
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-400 hover:text-orange-600"
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            <button 
              type="submit"
              disabled={loading}
              className={`w-full bg-orange-600 hover:bg-orange-700 text-white font-bold py-4 rounded-xl shadow-lg shadow-orange-200 flex items-center justify-center gap-2 transition-all active:scale-95 ${loading ? 'opacity-70 cursor-not-allowed' : ''}`}
            >
              {loading ? "Authenticating..." : (
                <>
                  <LogIn size={18} /> Access Portal
                </>
              )}
            </button>
          </form>

          <div className="bg-stone-50 p-4 border-t border-stone-100 text-center">
            <p className="text-[10px] text-stone-400 font-bold uppercase tracking-tighter">
              Admin System Secured by Firebase Auth & MNR Technologies
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;